"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const serviceOptions = [{
    id: "colon_hydrotherapy",
    label: "Colon Hydrotherapy"
  }, {
    id: "vitamin_injections",
    label: "Vitamin Injections/IVs"
  }, {
    id: "peptides",
    label: "Peptides"
  }, {
    id: "detox_services",
    label: "Detox Services"
  }, {
    id: "weight_loss",
    label: "Weight Loss Services"
  }, {
    id: "hormone_services",
    label: "Hormone Services"
  }, {
    id: "lab_testing",
    label: "Lab Testing Services"
  }, {
    id: "teeth_whitening",
    label: "Teeth Whitening Services"
  }, {
    id: "wellness_consultation",
    label: "Wellness Consultation"
  }];
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Mail className="size-6" />
        </div>
        <h3 className="font-default text-2xl font-medium text-card-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 font-default tracking-widest uppercase text-xs">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="border-0 border-b border-input bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#8FA88F] transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@moprotocol.com" className="border-0 border-b border-input bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#8FA88F] transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[0-9+\-\s()]*$/,
        message: "Invalid phone number format"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (432) 555-0199" className="border-0 border-b border-input bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#8FA88F] transition-colors" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs">Optional – for scheduling coordination</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">How can we help?</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[120px] w-full border-0 border-b border-input bg-transparent px-0 py-2 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-[#8FA88F] aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-default", "resize-y")} placeholder="Tell us about your wellness goals..." {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs">Inquiries regarding IV therapy, lab testing, or memberships.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="services_of_interest" render={() => <FormItem>
            <FormLabel className="font-default text-foreground mb-2 block">Services of Interest</FormLabel>
            <div className="space-y-3">
              {serviceOptions.map((option, i) => <FormField key={option.id} control={control} name="services_of_interest" render={({
          field
        }) => {
          return <FormItem key={option.id} data-index={i} className="flex flex-row items-center space-x-3 space-y-0 border-b border-input pb-2">
                        <FormControl>
                          <Checkbox checked={field.value?.includes(option.id)} onCheckedChange={checked => {
                return checked ? field.onChange([...field.value, option.id]) : field.onChange(field.value?.filter(value => value !== option.id));
              }} className="border-input data-[state=checked]:bg-[#8FA88F] data-[state=checked]:border-[#8FA88F]" />
                        </FormControl>
                        <FormLabel className="font-default text-sm font-normal cursor-pointer">
                          {option.label}
                        </FormLabel>
                      </FormItem>;
        }} />)}
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-[#8FA88F] text-primary-foreground hover:opacity-90 transition-opacity rounded-md font-default tracking-widest uppercase text-xs" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Processing...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ConnectWithUs() {
  return <section data-section-id="223899" id="connect-with-us" className="bg-background text-foreground relative overflow-hidden py-24">
      <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-5 blur-3xl">
        <div className="aspect-square w-[600px] rounded-lg bg-primary" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-primary mb-3 text-sm font-default font-semibold tracking-widest uppercase">
            Contact M / O Protocol
          </h2>
          <h3 className="font-default text-4xl font-medium tracking-tight sm:text-5xl text-primary">
            Begin Your Protocol
          </h3>
          <p className="text-muted-foreground mt-4 text-lg font-default">Connect with our wellness specialists in Midland, Granbury and nationwide through our virtual care platform to tailor your medical wellness journey.</p>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-3 lg:gap-8">
          <div className="flex flex-col gap-8 lg:col-span-1">
            <div className="flex items-start gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-card shadow-sm">
                <Phone className="text-primary size-5" />
              </div>
              <div>
                <h4 className="font-default text-lg font-medium text-primary">Get In Touch</h4>
                <p className="text-muted-foreground mt-1 text-sm font-default">Mon-Fri: 9 am-4 pm<br /><strong><em>After 4 pm & Saturday By Appt</em></strong></p>
                <Link to="tel:+14325550100" className="mt-2 block font-default font-medium transition-colors hover:text-primary/70 text-primary">+1 (432) 247-1660</Link>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-card shadow-sm">
                <Mail className="text-primary size-5" />
              </div>
              <div>
                <h4 className="font-default text-lg font-medium text-primary">Contact Us</h4>
                <p className="text-muted-foreground mt-1 text-sm font-default">
                  For scheduling & lab results
                </p>
                <Link to="mailto:hello@moprotocol.com" className="mt-2 block font-default font-medium transition-colors hover:text-primary/70 text-primary">
                  hello@moprotocol.com
                </Link>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-card shadow-sm">
                <MapPin className="text-primary size-5" />
              </div>
              <div>
                <h4 className="font-default text-lg font-medium text-primary">Our Locations</h4>
                <p className="text-muted-foreground mt-1 text-sm font-default">Midland, TX<br />2003 W. Wall St + Ste B</p>
                <address className="mt-2 block not-italic font-default font-medium text-primary"><br />Granbury, TX<br />1401 N. Plaza<br /><br /><div data-ast-id="f:/src/pages/Contact/sections/ConnectWithUs.tsx|sig:xcbdfc" data-ast-start="10248" data-ast-end="10417" contenteditable="false">Nationwide Consults</div></address>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-lg sm:p-8 lg:col-span-2">
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Connect With Us" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: "",
            services_of_interest: []
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out. A wellness coordinator will contact you shortly.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}